import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import { useSocket } from '../../services/socket';

// Interfaces
interface KitchenTotal {
  dishId: string;
  dishName: string;
  total: number;
  remaining?: number;
  allergies: Array<{
    tableNumber: number;
    allergyNames: string[];
    waiterName?: string;
  }>;
  notes: Array<{
    note: string;
    count: number;
    waiterNames?: string[];
  }>;
}

interface EventInfo {
  name: string;
  currentBreak: string;
  isActive: boolean;
  estimatedGuests?: number;
}

interface ConnectionStatus {
  isConnected: boolean;
  lastUpdate: Date;
  totalOrders: number;
}

const KitchenDisplay: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { socket, isConnected } = useSocket();

  const [eventInfo, setEventInfo] = useState<EventInfo>({
    name: '',
    currentBreak: '',
    isActive: false
  });

  const [kitchenTotals, setKitchenTotals] = useState<KitchenTotal[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false,
    lastUpdate: new Date(),
    totalOrders: 0
  });

  const [currentTime, setCurrentTime] = useState(new Date());
  const [adminMessage, setAdminMessage] = useState<string | null>(null);

  const loadActiveEvent = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/events/active');
      const data = await response.json();

      if (data.success && data.event) {
        setEventInfo({
          name: data.event.name,
          currentBreak: data.event.currentBreak || 'לא הוגדר',
          isActive: data.event.isActive,
          estimatedGuests: data.event.estimatedGuests
        });
        setConnectionStatus(prev => ({
          ...prev,
          isConnected: true,
          lastUpdate: new Date()
        }));
      } else {
        setEventInfo({
          name: '',
          currentBreak: '',
          isActive: false
        });
      }
    } catch (error) {
      console.error('Failed to load active event:', error);
      setConnectionStatus(prev => ({
        ...prev,
        isConnected: false
      }));
    }
  }, []);

  const loadKitchenTotals = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/orders/kitchen/totals');
      const data = await response.json();

      if (data.success && data.totals) {
        setKitchenTotals(data.totals);
        const totalOrders = data.totals.reduce((sum: number, dish: KitchenTotal) => sum + dish.total, 0);
        setConnectionStatus(prev => ({
          ...prev,
          totalOrders,
          lastUpdate: new Date()
        }));
      }
    } catch (error) {
      console.error('Failed to load kitchen totals:', error);
    }
  }, []);

  // Clock update effect
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(clockInterval);
  }, []);

  // Initial data load
  useEffect(() => {
    loadActiveEvent();
    loadKitchenTotals();
  }, [loadActiveEvent, loadKitchenTotals]);

  // Socket.io event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Join kitchen room for targeted updates
    socket.emit('join-room', 'kitchen');
    socket.emit('join-room', 'all');

    // Listen for kitchen totals updates
    const handleKitchenTotalsUpdated = (data: KitchenTotal[] | { totals: KitchenTotal[] }) => {
      const totals = Array.isArray(data) ? data : data.totals;
      if (totals) {
        setKitchenTotals(totals);
        const totalOrders = totals.reduce((sum: number, dish: KitchenTotal) => sum + dish.total, 0);
        setConnectionStatus(prev => ({
          ...prev,
          totalOrders,
          lastUpdate: new Date()
        }));
      }
    };

    // Listen for order events to refresh totals
    const handleOrderUpdate = () => {
      loadKitchenTotals();
    };

    // Listen for event state changes
    const handleEventUpdate = () => {
      loadActiveEvent();
      loadKitchenTotals();
    };

    // Listen for break changes
    const handleBreakChanged = (data: { eventId: string; breakName: string }) => {
      setEventInfo(prev => ({
        ...prev,
        currentBreak: data.breakName
      }));
      loadKitchenTotals(); // Refresh dish summary when break changes
    };

    // Listen for event end
    const handleEventEnded = () => {
      setEventInfo({
        name: '',
        currentBreak: '',
        isActive: false
      });
      setKitchenTotals([]);
    };

    socket.on('kitchen-totals-updated', handleKitchenTotalsUpdated);
    socket.on('order-created', handleOrderUpdate);
    socket.on('order-updated', handleOrderUpdate);
    socket.on('event-started', handleEventUpdate);
    socket.on('event-updated', handleEventUpdate);
    socket.on('break-changed', handleBreakChanged);
    socket.on('event-ended', handleEventEnded);

    // Listen for admin messages
    const handleNotification = (notification: { type: string; message: string; targetType: string }) => {
      if (notification.type === 'message' && ['all', 'kitchen'].includes(notification.targetType)) {
        setAdminMessage(notification.message);
        // Auto-hide after 10 seconds
        setTimeout(() => setAdminMessage(null), 10000);
      }
    };
    socket.on('notification', handleNotification);

    return () => {
      socket.emit('leave-room', 'kitchen');
      socket.off('kitchen-totals-updated', handleKitchenTotalsUpdated);
      socket.off('order-created', handleOrderUpdate);
      socket.off('order-updated', handleOrderUpdate);
      socket.off('event-started', handleEventUpdate);
      socket.off('event-updated', handleEventUpdate);
      socket.off('break-changed', handleBreakChanged);
      socket.off('event-ended', handleEventEnded);
      socket.off('notification', handleNotification);
    };
  }, [socket, loadKitchenTotals, loadActiveEvent]);

  // Update connection status based on socket state
  useEffect(() => {
    setConnectionStatus(prev => ({
      ...prev,
      isConnected
    }));
  }, [isConnected]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTotalAllergies = () => {
    return kitchenTotals.reduce((total, dish) => total + dish.allergies.length, 0);
  };

  const getUniqueAllergyTables = () => {
    const tables = new Set<number>();
    kitchenTotals.forEach(dish => {
      dish.allergies.forEach(allergy => tables.add(allergy.tableNumber));
    });
    return Array.from(tables).sort();
  };

  // No active event view
  if (!eventInfo.isActive) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-8">
            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          <h1 className="text-4xl font-bold text-gray-300 mb-4">
            ממתינים לתחילת אירוע
          </h1>

          <p className="text-xl text-gray-400 mb-8">
            המערכת תתעדכן אוטומטית עם תחילת האירוע
          </p>

          <div className="text-6xl font-mono text-blue-400 mb-4">
            {formatTime(currentTime)}
          </div>

          {/* Connection status */}
          <div className="flex items-center justify-center mt-8">
            <div className={`w-3 h-3 rounded-full mr-3 ${connectionStatus.isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${connectionStatus.isConnected ? 'text-green-400' : 'text-red-400'}`}>
              {connectionStatus.isConnected ? 'מחובר למערכת' : 'מתחבר למערכת...'}
            </span>
          </div>

          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="mt-8 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            יציאה
          </button>
        </div>
      </div>
    );
  }

  // Active event display
  return (
    <div className="h-screen bg-gray-900 text-white flex flex-col overflow-hidden">
      {/* Header - Large and clear */}
      <header className="bg-gray-800 border-b-2 border-blue-500 flex-shrink-0">
        <div className="px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Event Info */}
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">
                {eventInfo.name}
              </h1>
              <div className="flex items-center gap-6">
                <span className="text-2xl text-blue-400 font-medium">
                  {eventInfo.currentBreak}
                </span>
                {eventInfo.estimatedGuests && (
                  <span className="text-xl text-purple-400 font-medium">
                    {eventInfo.estimatedGuests} אורחים משוער
                  </span>
                )}
              </div>
            </div>

            {/* Status and Time */}
            <div className="text-left">
              <div className="text-5xl font-mono text-blue-400 mb-2">
                {formatTime(currentTime)}
              </div>

              <div className="flex items-center justify-end space-x-6 text-lg">
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full ml-3 ${connectionStatus.isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={connectionStatus.isConnected ? 'text-green-400' : 'text-red-400'}>
                    {connectionStatus.isConnected ? 'מחובר' : 'מנותק'}
                  </span>
                </div>

                <div className="text-gray-300">
                  סה״כ הזמנות: <span className="font-bold text-white">{connectionStatus.totalOrders}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Message Banner */}
      {adminMessage && (
        <div className="bg-yellow-500 text-black px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📢</span>
            <span className="text-xl font-bold">{adminMessage}</span>
          </div>
          <button
            onClick={() => setAdminMessage(null)}
            className="text-black hover:text-gray-700 text-2xl font-bold"
          >
            ✕
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 p-6 flex flex-col overflow-hidden">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-3 flex-shrink-0">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-4xl font-bold text-blue-400 mb-1">
              {kitchenTotals.reduce((sum, dish) => sum + dish.total, 0)}
            </div>
            <div className="text-base text-gray-300">סה״כ מנות</div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-4xl font-bold text-green-400 mb-1">
              {kitchenTotals.filter(dish => dish.total > 0).length}
            </div>
            <div className="text-base text-gray-300">מנות פעילות</div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-center">
            <div className="text-4xl font-bold text-purple-400 mb-1">
              {getUniqueAllergyTables().length}
            </div>
            <div className="text-base text-gray-300">שולחנות עם אלרגיות</div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 flex-1 min-h-0">
          {/* Dish Totals - Main section */}
          <div className="xl:col-span-2 flex flex-col min-h-0">
            <h2 className="text-2xl font-bold text-white mb-4 border-b border-gray-700 pb-2 flex-shrink-0">
              📋 סיכום מנות
            </h2>

            {kitchenTotals.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-xl">
                אין הזמנות עדיין
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto pr-2 flex-1">
                {kitchenTotals.map((dish) => (
                  <div
                    key={dish.dishId}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="text-3xl font-bold text-white mb-2">
                          {dish.dishName}
                        </h3>
                        {/* Notes breakdown */}
                        {dish.notes && dish.notes.length > 0 && (
                          <div className="space-y-1 mr-4">
                            {dish.notes.map((noteItem, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-lg text-purple-400">
                                <span className="text-gray-500">└─</span>
                                <span>{noteItem.note}</span>
                                <span className="text-purple-300 font-bold">({noteItem.count})</span>
                                {noteItem.waiterNames && noteItem.waiterNames.length > 0 && (
                                  <span className="text-gray-400 text-base">
                                    ({noteItem.waiterNames.join(', ')})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="text-left mr-6">
                        <div className="text-6xl font-bold text-blue-400 mb-1">
                          {dish.total}
                        </div>
                        {dish.remaining !== undefined && (
                          <div className="text-lg text-gray-400">
                            נותרו: <span className="font-bold text-white">{dish.remaining}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Allergies Section */}
          <div className="xl:col-span-1 flex flex-col min-h-0">
            <h2 className="text-2xl font-bold text-white mb-4 border-b border-gray-700 pb-2 flex-shrink-0">
              ⚠️ אלרגיות
            </h2>

            {getTotalAllergies() === 0 ? (
              <div className="bg-green-800 border border-green-600 rounded-lg p-4 text-center">
                <div className="text-xl font-bold text-green-100 mb-1">✅</div>
                <div className="text-lg text-green-100">
                  אין אלרגיות
                </div>
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto flex-1">
                {getUniqueAllergyTables().map(tableNumber => {
                  // Get all allergies and waiter name for this table
                  let waiterName = '';
                  const tableAllergies = kitchenTotals
                    .flatMap(dish =>
                      dish.allergies
                        .filter(allergy => allergy.tableNumber === tableNumber)
                        .map(allergy => {
                          if (allergy.waiterName) waiterName = allergy.waiterName;
                          return allergy.allergyNames;
                        })
                        .flat()
                    );

                  const uniqueAllergies = [...new Set(tableAllergies)];

                  return (
                    <div
                      key={tableNumber}
                      className="bg-orange-800 border border-orange-600 rounded-lg p-4"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-2xl font-bold text-orange-100">
                          שולחן {tableNumber}
                        </div>
                        {waiterName && (
                          <div className="text-lg text-orange-200">
                            {waiterName}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {uniqueAllergies.map((allergy, index) => (
                          <span
                            key={index}
                            className="bg-orange-600 text-orange-100 px-3 py-1 rounded-full text-lg font-medium"
                          >
                            {allergy}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}

              </div>
            )}
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="flex-shrink-0 px-6 py-3 border-t border-gray-700 bg-gray-800">
        <div className="flex justify-between items-center text-gray-400 text-sm">
          <div>
            עדכון אחרון: {connectionStatus.lastUpdate.toLocaleTimeString('he-IL')}
          </div>
          <div className="flex items-center space-x-4">
            <span>תצוגת מטבח - Giggo</span>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="mr-4 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg transition-colors text-sm"
            >
              יציאה
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KitchenDisplay;